<template>
    <!-- 页眉 -->
    <header-index />

    <!-- 文章列表区域 -->
    <div class="content-area bg-gray-50 py-8">
        <div class="container mx-auto px-4">
            <!-- 文章列表 -->
            <div 
                v-if="articlesPage.rows && articlesPage.rows.length > 0" 
                class="grid gap-6 grid-cols-1 lg:grid-cols-2 xl:grid-cols-3"
            >
                <article 
                    v-for="(article, index) in articlesPage.rows" 
                    :key="article.id || index"
                    class="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all">
                    <div class="flex flex-row items-stretch h-[260px]">
                        <!-- 左侧图片 -->
                        <div class="w-2/5 flex-shrink-0 h-full">
                            <nuxt-link :to="article.fullUrl" class="block relative w-full h-full">
                                <img 
                                    :src="cmsUtils.isEmpty(article.imageUrl) ? '/images/xxx.jpg' : article.imageUrl!.split(',')[0]" 
                                    :alt="article.imageAlt || article.title" 
                                    class="w-full h-full object-cover"
                                    loading="lazy"
                                    width="400"
                                    height="260"
                                >
                            </nuxt-link>
                        </div>

                        <!-- 右侧内容 -->
                        <div class="w-3/5 p-6 flex flex-col justify-between h-full">
                            <div>
                                <!-- 标题 -->
                                <h2 class="text-xl md:text-2xl font-weight-600 mb-3 line-clamp-2">
                                    <nuxt-link 
                                        :to="article.fullUrl" 
                                        class="text-gray-900 hover:text-green-600 transition-colors">
                                        {{ article.title }}
                                    </nuxt-link>
                                </h2>

                                <!-- 描述 -->
                                <p class="text-gray-600 mb-4 line-clamp-3 text-sm md:text-base">
                                    {{ article.description }}
                                </p>

                                <!-- 标签和元信息 -->
                                <div class="flex flex-wrap items-center gap-3 mb-4">
                                    <!-- 标签 -->
                                    <div v-if="article.tagsList && article.tagsList.length > 0" class="flex flex-wrap gap-2">
                                        <span 
                                            v-for="tag in article.tagsList.slice(0, 3)" 
                                            :key="tag.id"
                                            class="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-md">
                                            {{ tag.name }}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <!-- 更新时间 + 阅读更多按钮 -->
                            <div class="flex items-center justify-between gap-4 flex-wrap">
                                <span v-if="article.updateTime" class="text-xs text-gray-500">
                                    {{ article.updateTime.slice(0, 10) }}
                                </span>
                                <nuxt-link 
                                    :to="article.fullUrl" 
                                    class="inline-block px-6 py-2 bg-green-600 text-white rounded-md text-sm font-weight-400 hover:bg-green-700 transition-colors">
                                    Read More
                                </nuxt-link>
                            </div>
                        </div>
                    </div>
                </article>
            </div>

            <!-- 空状态 -->
            <div v-else class="text-center py-12">
                <p class="text-gray-500 text-lg">No articles found.</p>
            </div>

            <!-- 分页 -->
            <div v-if="articlesPage.rows && articlesPage.rows.length > 0" class="mt-12">
                <page-icon 
                    :page-num="props.pageNum" 
                    :page-size="props.pageSize" 
                    :total="articlesPage.total!" />
            </div>
        </div>
    </div>

    <!-- 页脚 -->
    <footer-index />
</template>

<script setup lang="ts" name="TagsDefault">
    import { ref, watch } from 'vue'
    import type { Category, Tags, Page, Article } from '~/types';
    import headerIndex from '../../components/header/header-index.vue'
    import footerIndex from '../../components/footer/footer-index.vue'
    import pageIcon from '../../components/common/pageIcon.vue';
    import cmsUtils from '~/utils/cmsUtils';

    const cmsInfo = await cmsUtils.getCmsInfo()

    // 接收分类数据
    const props = withDefaults(defineProps<{
        tagsList?: Tags[]
        pageNum?: number
        pageSize?: number
    }>(), {
        tagsList: () => [],
        pageNum: 1,
        pageSize: 12
    })

    // 文章列表数据
    const articlesPage = ref<Page<Article>>({ rows: [], total: 0, pageNum: 1, pageSize: 12 })

    // 获取文章列表的函数
    const fetchArticles = async (pageNum: number) => {
        // tagsIdsList = [[1,4,6],[208]]   这个测试，获得的值是正确的。
        // 这里的tagsIdsList 是一个tagsIds 的List，是一个二维数组
        // 每个元素包含该 tag 及其所有子孙 tag 的 ID
        const tagsIdsList: number[][] = []
        props.tagsList.forEach(item => {
            const childTagsIds = [item.id, ...cmsUtils.getChildTagsIds(item!.id, cmsInfo)]
            tagsIdsList.push(childTagsIds)
        })

        const body = {
            "websiteId": cmsInfo!.website.id,
            "tagsIdsList": tagsIdsList,
            "pageNum": pageNum,
            "pageSize": props.pageSize
        }

        const response = await useHttp("/nuxt/article/list", {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: body
        }) as Page<Article>;
        
        response.rows.forEach((article: Article) => {
            let articleCategory: Category = cmsInfo.category.find(item => item.id == article.categoryId)!;
            article.fullUrl = cmsUtils.appendCategoryUrl(articleCategory, cmsInfo, "/" + article.titleUrl);
        });

        articlesPage.value = response
    }

    // 初始加载
    await fetchArticles(props.pageNum)

    // 监听 pageNum 变化，重新获取数据
    watch(() => props.pageNum, async (newPageNum) => {
        await fetchArticles(newPageNum)
    })
</script>

<style scoped lang="scss">
    /* 让中间内容区域撑满剩余空间 */
    .content-area {
        flex: 1;
        display: flex;
        flex-direction: column;
    }

    /* 文本截断样式 */
    .line-clamp-2 {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }

    .line-clamp-3 {
        display: -webkit-box;
        -webkit-line-clamp: 3;
        line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }
</style>
